/**
 * Date Utility Functions
 * 
 * All dates from the backend are in UTC format.
 * These utilities handle conversion to local time for display.
 */

/**
 * Parse a UTC date string from the backend and return a Date object in local time
 * Handles various UTC formats:
 * - ISO 8601 with Z: "2025-10-20T14:35:22.123Z"
 * - Java LocalDateTime: "2025-10-20T14:35:22.123456"
 * - Date only: "2025-10-20"
 * - Date objects (returns as-is)
 * 
 * @param {string|Date} utcDateString - UTC date string from backend or Date object
 * @returns {Date|null} Date object in local timezone, or null if invalid
 */
export const parseUTCDate = (utcDateString) => {
    if (!utcDateString) {
        return null;
    }

    // If already a Date object, return it
    if (utcDateString instanceof Date) {
        return isNaN(utcDateString.getTime()) ? null : utcDateString;
    }

    try {
        let dateObj;
        
        // Handle different UTC formats from backend
        if (utcDateString.endsWith('Z')) {
            // Already in ISO format with Z
            dateObj = new Date(utcDateString);
        } else if (utcDateString.includes('T')) {
            // LocalDateTime format without Z - add Z to indicate UTC
            dateObj = new Date(utcDateString + 'Z');
        } else {
            // Date only format or other - let Date constructor handle it
            dateObj = new Date(utcDateString);
        }
        
        // Check if date is valid
        if (isNaN(dateObj.getTime())) {
            console.warn('Invalid date string:', utcDateString);
            return null;
        }

        return dateObj;
    } catch (error) {
        console.error('Error parsing date:', utcDateString, error);
        return null;
    }
};

/**
 * Format a UTC date string or Date object to local date and time
 * @param {string|Date} utcDateOrString - UTC date string from backend or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted local date and time string
 */
export const formatLocalDateTime = (utcDateOrString, options = {}) => {
    // Handle Date objects directly
    const date = utcDateOrString instanceof Date 
        ? utcDateOrString 
        : parseUTCDate(utcDateOrString);
    
    if (!date || isNaN(date.getTime())) {
        return 'Invalid date';
    }

    const defaultOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        ...options
    };

    return date.toLocaleString(undefined, defaultOptions);
};

/**
 * Format a UTC date string or Date object to local date only
 * @param {string|Date} utcDateOrString - UTC date string from backend or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted local date string
 */
export const formatLocalDate = (utcDateOrString, options = {}) => {
    // Handle Date objects directly
    const date = utcDateOrString instanceof Date 
        ? utcDateOrString 
        : parseUTCDate(utcDateOrString);
    
    if (!date || isNaN(date.getTime())) {
        return 'Invalid date';
    }

    const defaultOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        ...options
    };

    return date.toLocaleDateString(undefined, defaultOptions);
};

/**
 * Format a UTC date string or Date object to local time only
 * @param {string|Date} utcDateOrString - UTC date string from backend or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted local time string
 */
export const formatLocalTime = (utcDateOrString, options = {}) => {
    // Handle Date objects directly
    const date = utcDateOrString instanceof Date 
        ? utcDateOrString 
        : parseUTCDate(utcDateOrString);
    
    if (!date || isNaN(date.getTime())) {
        return 'Invalid time';
    }

    const defaultOptions = {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        ...options
    };

    return date.toLocaleTimeString(undefined, defaultOptions);
};

/**
 * Format a UTC timestamp to relative time (e.g., "2 hours ago")
 * @param {string|Date} utcDateOrString - UTC date string from backend or Date object
 * @returns {string} Relative time string
 */
export const formatTimeAgo = (utcDateOrString) => {
    // Handle Date objects directly
    const date = utcDateOrString instanceof Date 
        ? utcDateOrString 
        : parseUTCDate(utcDateOrString);
    
    if (!date || isNaN(date.getTime())) {
        return 'Unknown time';
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    // Handle future dates or negative differences
    if (diffInSeconds < 0) {
        return 'Just now';
    }

    if (diffInSeconds < 60) {
        return 'Just now';
    } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 604800) {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) { // Less than 30 days
        const weeks = Math.floor(diffInSeconds / 604800);
        return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else {
        return formatLocalDate(utcDateString);
    }
};

/**
 * Convert an object's date fields from UTC to local Date objects
 * Recursively processes nested objects and arrays
 * 
 * @param {Object|Array} obj - Object or array with UTC date strings
 * @param {string[]} dateFields - Array of field names that contain dates (default: common date fields)
 * @returns {Object|Array} New object/array with dates converted to Date objects
 */
export const convertUTCDatesToLocal = (obj, dateFields = ['createdAt', 'updatedAt', 'lastModifiedAt', 'orderDate', 'receiveDate', 'reportDate', 'timestamp', 'lastTaggedAt', 'contactCreatedAt', 'expiresAt']) => {
    if (!obj) {
        return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
        return obj.map(item => convertUTCDatesToLocal(item, dateFields));
    }

    // Handle objects
    if (typeof obj === 'object') {
        const converted = { ...obj };
        
        for (const [key, value] of Object.entries(converted)) {
            // If this field is a date field, convert it
            if (dateFields.includes(key) && typeof value === 'string') {
                converted[key] = parseUTCDate(value);
            }
            // Recursively process nested objects and arrays
            else if (typeof value === 'object' && value !== null) {
                converted[key] = convertUTCDatesToLocal(value, dateFields);
            }
        }
        
        return converted;
    }

    return obj;
};

/**
 * Get a Date object for the start of today in UTC
 * @returns {Date} Start of today in UTC
 */
export const getTodayStartUTC = () => {
    const now = new Date();
    const utcDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
    return utcDate;
};

/**
 * Get a Date object for the end of today in UTC
 * @returns {Date} End of today in UTC
 */
export const getTodayEndUTC = () => {
    const now = new Date();
    const utcDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
    return utcDate;
};

/**
 * Convert a local Date object to ISO string for backend (UTC)
 * @param {Date} localDate - Local date object
 * @returns {string} ISO 8601 string in UTC
 */
export const toUTCString = (localDate) => {
    if (!localDate || !(localDate instanceof Date)) {
        return null;
    }
    return localDate.toISOString();
};

/**
 * Convert local datetime-local input value to UTC ISO string
 * @param {string} localDateTimeString - Local datetime string from input (YYYY-MM-DDTHH:mm)
 * @returns {string|null} UTC ISO string
 */
export const convertLocalInputToUTC = (localDateTimeString) => {
    if (!localDateTimeString) return null;
    // The datetime-local input gives us a string in local time
    // We create a Date object which will interpret it as local time
    const localDate = new Date(localDateTimeString);
    // Check if date is valid
    if (isNaN(localDate.getTime())) {
        return null;
    }
    // Convert to UTC ISO string for backend
    return localDate.toISOString();
};

/**
 * Convert an object's Date objects to UTC ISO strings for backend submission
 * Recursively processes nested objects and arrays
 * 
 * @param {Object|Array} obj - Object or array with Date objects
 * @param {string[]} dateFields - Array of field names that should be converted to UTC strings
 * @returns {Object|Array} New object/array with Date objects converted to UTC ISO strings
 */
export const convertLocalDatesToUTC = (obj, dateFields = ['createdAt', 'updatedAt', 'lastModifiedAt', 'orderDate', 'receiveDate', 'reportDate', 'effectiveFrom', 'expiration', 'expiresAt']) => {
    if (!obj) {
        return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
        return obj.map(item => convertLocalDatesToUTC(item, dateFields));
    }

    // Handle objects
    if (typeof obj === 'object') {
        const converted = { ...obj };
        
        for (const [key, value] of Object.entries(converted)) {
            // If this field is a date field and is a Date object, convert to UTC ISO string
            if (dateFields.includes(key)) {
                if (value instanceof Date) {
                    converted[key] = value.toISOString();
                } else if (typeof value === 'string' && value.includes('T')) {
                    // If it's a datetime-local string (YYYY-MM-DDTHH:mm), convert to UTC
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                        converted[key] = date.toISOString();
                    }
                }
            }
            // Recursively process nested objects and arrays
            else if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
                converted[key] = convertLocalDatesToUTC(value, dateFields);
            }
        }
        
        return converted;
    }

    return obj;
};

/**
 * Format date string for display in tables and lists
 * Convenience function that uses formatLocalDateTime with sensible defaults
 * @param {string|Date} dateValue - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateString = (dateValue) => {
    if (!dateValue) return '-';
    return formatLocalDateTime(dateValue);
};
